import React from 'react';

export type PublicMaintenanceSummary = {
  visibility: string;
  record_count: number;
  latest_title?: string | null;
  latest_date?: string | null;
  latest_category?: string | null;
};

export type PublicMaintenanceRecord = {
  title?: string;
  service_date?: string;
  category?: string;
  mileage?: number | null;
};

type Props = {
  summary: PublicMaintenanceSummary;
  records?: PublicMaintenanceRecord[];
  accentHex?: string;
};

/**
 * Public build service-history surface. Never shows costs, notes, or receipts.
 */
const VehicleMaintenanceSection: React.FC<Props> = ({ summary, records = [], accentHex }) => {
  const showRecords = summary.visibility === 'public_records' && records.length > 0;

  return (
    <section id="maintenance" className="space-y-4 scroll-mt-24" aria-label="Service history">
      <div className="build-section-intro">
        <p className="kicker mb-2" style={accentHex ? { color: accentHex } : undefined}>
          Service history
        </p>
        <h2 className="heading-section text-xl mb-2">Maintenance</h2>
        <p className="text-sm text-gray-400 max-w-xl">
          {summary.record_count > 0
            ? `${summary.record_count} documented service${summary.record_count === 1 ? '' : 's'} on this build.`
            : 'The owner has shared a service-history summary for this build.'}
        </p>
      </div>

      {summary.latest_title ? (
        <div className="rounded-2xl border border-white/[0.08] bg-[#0c0c0c]/40 px-4 py-3 sm:px-5">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Latest service</p>
          <p className="text-white font-medium">{summary.latest_title}</p>
          <p className="text-sm text-gray-400 mt-1">
            {[summary.latest_date, summary.latest_category].filter(Boolean).join(' · ')}
          </p>
        </div>
      ) : null}

      {showRecords ? (
        <ul className="space-y-2" aria-label="Public service records">
          {records.map((r, i) => (
            <li
              key={`${r.service_date ?? 'd'}-${r.title ?? 't'}-${i}`}
              className="rounded-xl border border-white/[0.06] px-4 py-3 flex flex-wrap items-baseline justify-between gap-2"
            >
              <span className="text-sm text-white">{r.title || 'Service'}</span>
              <span className="text-xs text-gray-500">
                {[r.service_date, r.category, r.mileage != null ? `${r.mileage.toLocaleString()} mi` : null]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
};

export default VehicleMaintenanceSection;
